import { useState } from 'react';
import { getSubdomainsForDomains } from '@/constants/contentDomains';
import { TContentDomain } from '@/types/Content.types';
import { TArticleSectionDraft, TContentFormErrors, TContentFormValues } from '@/modules/content/creation/model/types';

const buildInitialValues = (defaults?: Partial<TContentFormValues>): TContentFormValues => ({
  title: defaults?.title ?? '',
  description: defaults?.description ?? '',
  domains: defaults?.domains ?? [],
  subdomains: defaults?.subdomains ?? [],
  isPublic: defaults?.isPublic ?? false,
  body: defaults?.body ?? '',
  sections: defaults?.sections ?? [],
  videoSource: defaults?.videoSource ?? 'mux',
  videoFile: null,
  videoProcessingError: defaults?.videoProcessingError ?? null,
  youtubeUrl: defaults?.youtubeUrl ?? '',
  fullDescription: defaults?.fullDescription ?? '',
  muxStatus: defaults?.muxStatus ?? null,
  muxPlaybackId: defaults?.muxPlaybackId ?? null,
  originalVideoSource: defaults?.originalVideoSource ?? null,
  originalYoutubeUrl: defaults?.originalYoutubeUrl ?? '',
  hasOriginalRemoteVideo: defaults?.hasOriginalRemoteVideo ?? false,
});

// Owns the content form model (field values, thumbnail, validation errors) so the
// wizard view can focus on step navigation and rendering.
export const useContentForm = (defaults?: Partial<TContentFormValues>) => {
  const [values, setValues] = useState<TContentFormValues>(() => buildInitialValues(defaults));
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<TContentFormErrors>({});

  const setField = <K extends keyof TContentFormValues>(key: K, value: TContentFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const setSections = (sections: TArticleSectionDraft[]) => setField('sections', sections);

  // Changing the selected domains drops any subdomains that no longer belong to the set.
  const setDomains = (domains: TContentDomain[]) =>
    setValues((prev) => {
      const allowed = getSubdomainsForDomains(domains);
      return {
        ...prev,
        domains,
        subdomains: prev.subdomains.filter((subdomain) => allowed.includes(subdomain)),
      };
    });

  const setSubdomains = (subdomains: string[]) => setField('subdomains', subdomains);

  return {
    values,
    setField,
    setSections,
    setDomains,
    setSubdomains,
    thumbnailFile,
    setThumbnailFile,
    errors,
    setErrors,
  };
};
